// apps/widget/src/lib/remote-capture.ts
//
// The photo a phone took, brought to the desktop that is showing the report.
//
// A desktop capture keeps its own frame in memory for the report's annotated
// view. A phone capture never passes through the desktop, so the report had
// scores and no face. The server sends the desktop a link to the upload that
// lasts two minutes; this reads it once, while analysis runs and before the
// upload is deleted, and from then on it is held exactly like a desktop
// capture: an object URL in this tab, revoked when the widget closes.
//
// The face is found here, on the photo itself, so the phone never has to send
// its face geometry anywhere — that still only leaves the phone when the
// visitor agreed to keep their photo.

import type { CapturedFace } from '../state';
import { detect, loadFaceMesh } from './facemesh';

const inFlight = new Map<string, Promise<CapturedFace | null>>();

/** Null when the photo cannot be read or holds no face; the report then shows scores alone. */
export function captureFromUrl(url: string): Promise<CapturedFace | null> {
  let pending = inFlight.get(url);
  if (!pending) {
    pending = load(url);
    inFlight.set(url, pending);
  }
  return pending;
}

async function load(url: string): Promise<CapturedFace | null> {
  try {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) return null;
    const blob = await response.blob();

    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
    bitmap.close();

    if (!(await loadFaceMesh())) return null;
    const face = detect(canvas, performance.now());
    if (!face) return null;

    return {
      photoUrl: URL.createObjectURL(blob),
      width: canvas.width,
      height: canvas.height,
      landmarks: face.landmarks,
    };
  } catch {
    return null;
  }
}
